import { LoadingDialogComponent } from '../components/loading-dialog/loading-dialog.component';
import { Injectable } from '@angular/core';
import { I18nService } from '@ppl/i18n';
import { AsyncState } from '@ppl/store';
import { ButtonType } from '@ppl/ui/button';
import { PplDialogService, PplYesNoCancelResult } from '@ppl/ui/dialog';
import { concat, of, throwError } from 'rxjs';
import {
  catchError,
  finalize,
  map,
  share,
  switchMap,
  tap
} from 'rxjs/operators';
import type { AsyncValue } from '@ppl/store';
import type { PplDialogMessageOptions, PplDialogRef } from '@ppl/ui/dialog';
import type { Observable } from 'rxjs';
import type { LoadingDialogData } from '../components/loading-dialog/loading-dialog.component';

@Injectable()
export class ActionsUtilsService {

  constructor(
    private dialogService: PplDialogService,
    private i18nService: I18nService
  ) { }

  doAction<T>(observable: Observable<T>, options: DoActionOptions = {}) {
    let loadingDialogRef: PplDialogRef;

    return doAction(of(null).pipe(
      switchMap(() => {
        if (options.confirmMessage) {
          return this.dialogService.confirm({
            text: options.confirmMessage,
            description: options.confirmMessageDescription,
            actionButtonType: options.confirmMessageActionButtonType,
            actionLabel: options.confirmMessageActionLabel
          });
        }

        return of(null);
      }),
      tap(() => {
        if (!options.silent) {
          loadingDialogRef = this.dialogService.open<LoadingDialogComponent, LoadingDialogData>(
            LoadingDialogComponent,
            {
              data: {
                text$: options.loadingDialogText$
              }
            }
          );
        }
      }),
      switchMap(() => {
        return observable;
      }),
      catchError(error => {
        if (loadingDialogRef) {
          loadingDialogRef.close();
        }

        return throwError(error);
      }),
      tap(() => {
        if (loadingDialogRef) {
          loadingDialogRef.close();
        }
      }),
      finalize(() => {
        if (loadingDialogRef) {
          loadingDialogRef.close();
        }
      }),
      share()
    ));
  }

  saveChangesAction<T>(options: SaveChangesActionOptions<T>) {
    if (options.changes) {
      this.dialogService.yesNoCancel({
        text: options.text || this.i18nService.translate('Do_you_want_to_save_your_changes'),
        description: options.description,
        yesLabel: this.i18nService.translate('Save'),
        noLabel: this.i18nService.translate('Discard'),
        noButtonType: ButtonType.Button5
      }).subscribe(result => {
        switch (result) {
          case PplYesNoCancelResult.Yes:
            this.doAction(options.onSave(), {
              silent: options.silent
            }).subscribe(result => {
              options.onAction(result);
            });
            break;
          case PplYesNoCancelResult.No:
            options.onAction();
            break;
          case PplYesNoCancelResult.Cancel:
            break;
        }
      });
    } else {
      options.onAction();
    }
  }

  openOverlay() {
    return this.dialogService.open(LoadingDialogComponent, {
      data: {
        hideLoading: true
      }
    });
  }


  // shorthand for confirm + delete options preset
  confirmDelete(options: PplDialogMessageOptions & { onlyPositive?: boolean; }) {
    return this.dialogService.confirm({
      ...options,
      actionLabel: this.i18nService.translate('Delete'),
      actionButtonType: ButtonType.Button5,
      onlyPositive: options.onlyPositive
    });
  }
}

export function fetchAsyncValue<T>(observable: Observable<T>): Observable<AsyncValue<T>> {
  return concat(
    of({
      state: AsyncState.FETCHING
    }),
    observable.pipe(
      map(data => {
        if (!data) {
          return {
            state: AsyncState.ERROR,
            error: null
          };
        }

        return {
          state: AsyncState.FETCHED,
          value: data
        };
      }),
      catchError(error => {
        return concat(
          of({
            state: AsyncState.ERROR,
            error
          }),
          throwError(error)
        );
      })
    )
  );
}

function doAction<T>(action$: Observable<T>) {
  action$.subscribe();

  return action$;
}

export interface DoActionOptions {
  confirmMessage?: string;
  confirmMessageDescription?: string;
  confirmMessageActionButtonType?: ButtonType;
  confirmMessageActionLabel?: string;
  silent?: boolean;
  loadingDialogText$?: Observable<string>;
}

export interface SaveChangesActionOptions<T = any> {
  changes: boolean;
  onAction: (save?: T) => void;
  onSave: () => Observable<T>;
  silent?: boolean;
  text?: string;
  description?: string;
}
